//Component
import AppDispatcher from '../core/AppDispatcher.jsx';

//Constants
import ApplicationConstants from '../constants/ApplicationConstants';
import Constants from '../constants/PensionPaymentConstants.js';
import FundingYourProductConstants from '../constants/FundingYourProductConstants.js';
import PensionTypeConstants from '../constants/PensionTypeConstants.js';
import ContentService from '../services/ContentService.js';

//Utilities
import { EventEmitter } from 'events';
import _ from 'lodash';
import Mapper from '../utils/Mapper.js';
import Helper from '../utils/PensionPaymentHelper.js';
import Validators from '../utils/Validators.js';
import Formatter from '../utils/Formatter.js';
import DateTime from '../utils/DateTime.js';

const ActionTypes = Constants.ActionTypes;
const CHANGE_EVENT = 'change';

let _showBankAccountDetailsMsg = false,
    _isWithinFinancialYear = false,
    _isMonthOtherThanJune = false,
    _isPreRetirement = false,
    _isCustomOrderDisabled = false,
    _showInvAllocationOptions = true,
    _showInSpecieOptions = false,

    _model,
    _summaryError,
    _formError,
    _productId,
    _frequencyDatesData,
    _bankAccounts,
    _paymentLevelsOption,
    _annualIncreaseOption,
    _inSpecieOptions,
    _invAllocationOptions,
    _investmentOptionList,
    _emptyOrder;

let _customAttributeName = {
    accountStartDate: 'Pension commencement date',
    accountHolderName: 'Name of account holder',
    branchNumber: 'Branch number (BSB)',
    accountNumber: 'Bank account number',
    yearlyFrequencyDate: 'An answer to When would you like the payment to be made?',
    deferPayments: 'Defer payments',
    paymentGrossAmount: 'Pension payment amount',
    annualPercentage: 'Annual increase percentage',
    drawDownTypeOption: 'An answer to How would you like to draw down your payments?'
};

function updatePensionPayment(data) {

    if (data) {
        Mapper.objectMerger(_model, data);
    }
}

function setAccStartOpt(value) {

    if (value === Constants.AccountStart.ASAP) {
        _model[Constants.FieldNames.ACC_START_DATE] = DateTime.dateToString(new Date());
    } else {
        _model[Constants.FieldNames.ACC_START_DATE] = '';
        Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.ACC_START_DATE);
    }
}

function updateBankAccount(value) {

    if (value === Constants.nominateOtherAccount) {
        updatePensionPayment({
            accountHolderName: '', branchNumber: '', accountNumber: ''
        });
    } else {
        if (value) {
            updatePensionPayment({
                accountHolderName: _bankAccounts[0].accountHolder, branchNumber: _bankAccounts[0].branchNumber, accountNumber: _bankAccounts[0].accountNumber
            });
        } else {
            updatePensionPayment({
                accountHolderName: '', branchNumber: '', accountNumber: ''
            });
        }
    }

    Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.NAME_ACC_HOLDER);
    Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.BRANCH_NUMBER);
    Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.ACC_NUMBER);
}

function getRules() {

    let rules = {
        accountStartDateOption: 'required',
        accountStartDate: 'required|dateInvalidFormat|shouldBeTodayOrFutureDate',
        paymentFrequency: 'required',
        yearlyFrequencyDate: 'required'
    };

    if (_model.selectedBankAccount === Constants.nominateOtherAccount ||
        _model.selectedBankAccount === '') {
        rules['accountHolderName'] = 'nameWithNumSpaces|maxLength:30';
        rules['branchNumber'] = 'numeric2|exactLength:6';
        rules['accountNumber'] = 'alphaNumWithSpaces|maxLength:9';
    }

    if (_model[Constants.FieldNames.DRAW_DOWN_OPTION] === Constants.DrawDownOption.CUSTOM &&
        _model[Constants.FieldNames.INVESTMENT_OPTION_LIST].length) {
        rules['drawDownTypeOption'] = 'required';
    }

    if (_model[Constants.FieldNames.PAYMENT_LEVEL_OPTIONS] === Constants.PaymentLevels.SPECIFIC) {
        rules['paymentGrossAmount'] = 'required|currency';
    }

    if (_model[Constants.FieldNames.ANNUAL_INCREASE_OPTIONS] === Constants.AnnualIncrease.SET) {
        rules['annualPercentage'] = 'required|numeric2|percentMaxLength15';
    }

    if (_model[Constants.FieldNames.INVESTMENT_OPTION_LIST].length &&
        _model[Constants.FieldNames.DRAW_DOWN_OPTION] === Constants.DrawDownOption.CUSTOM) {
        rules['deferPayments'] = 'required';
    }

    return rules;
}

function getValidationRules(fieldName) {

    let allRules = getRules(),
        rulesForField = {};

    if (!fieldName) {
        return allRules;
    }

    if (!allRules[fieldName]) {
        return null;
    }

    rulesForField[fieldName] = allRules[fieldName];

    return rulesForField;
}

function hasError(fieldName) {

    let index = _.findIndex(_formError.errorList, function(data) { return data.id === Constants.pensPayment + fieldName; });
    return index !== -1;
}

function showOrHideBankAccountMsg() {
    _showBankAccountDetailsMsg = !hasError(Constants.FieldNames.NAME_ACC_HOLDER) && _model[Constants.FieldNames.NAME_ACC_HOLDER] &&
                                !hasError(Constants.FieldNames.BRANCH_NUMBER) && _model[Constants.FieldNames.BRANCH_NUMBER] &&
                                !hasError(Constants.FieldNames.ACC_NUMBER) && _model[Constants.FieldNames.ACC_NUMBER] &&
                                (_model[Constants.FieldNames.SELECTED_BANK_ACC] === Constants.nominateOtherAccount || !_model[Constants.FieldNames.SELECTED_BANK_ACC]);
}

function validateFields(fieldName, fieldValue, componentId) {

    let rules = getValidationRules(fieldName),
        data = {};

    data[fieldName] = fieldValue;

    Validators.validateField(data, rules, _customAttributeName, _formError.errorList, fieldName, componentId);
}

function updatePaymentFrequency(fieldId, value) {

    _model[Constants.FieldNames.YEARLY_FREQUENCY_DATE] = '';
    if (value !== Constants.PaymentFrequency.YEARLY) {
        _model[Constants.FieldNames.DEFER_PAYMENTS_TEXT] = '';
        _model[Constants.FieldNames.DEFER_PAYMENTS_WITHIN_FYEAR_TEXT] = '';
        Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.YEARLY_FREQUENCY_DATE);
    } else {
        updatePaymentFrequencyDate(_model[Constants.FieldNames.YEARLY_FREQUENCY_DATE]);
    }
}

function updatePaymentFrequencyDate(value) {

    let dateJson;

    Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.YEARLY_FREQUENCY_DATE);

    if (value) {
        dateJson = DateTime.dateStringToJson(value);

        if ((dateJson.month > Constants.june && DateTime.isCurrentYear(dateJson.year)) ||
            (dateJson.month <= Constants.june && !DateTime.isCurrentYear(dateJson.year))) {
            _isWithinFinancialYear = true;
        } else {
            _isWithinFinancialYear = false;
        }

        _isMonthOtherThanJune = dateJson.month !== Constants.june;
        updateDeferPayments(_model[Constants.FieldNames.DEFER_PAYMENTS]);
    }
}

function updatePaymentLevelOptions(value) {

    if (value !== Constants.PaymentLevels.SPECIFIC) {
        updatePensionPayment({
            paymentGrossAmount: '', annualIncreaseOption: 'increase', annualPercentage: ''
        });
        if (value === Constants.PaymentLevels.MINIMUM) {
            updatePensionPayment({ annualIncreaseOption: '' });
        }
        Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.PAYMENT_GROSS_AMOUNT);
        Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.ANNUAL_INCREASE_OPTIONS);
        Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.ANNUAL_PERCENTAGE);
    }
}

function updateAnnualIncreaseOptions(value) {

    if (value !== Constants.AnnualIncrease.SET) {
        _model[Constants.FieldNames.ANNUAL_PERCENTAGE] = '';
        Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.ANNUAL_PERCENTAGE);
    }
}

function updateAnnualPercentage(value) {

    let percentage = parseInt(value, 10);

    if (percentage > 0 && percentage < 10) {
        _model[Constants.FieldNames.ANNUAL_PERCENTAGE] = percentage;
    }
}

function updateDrawDownOption() {

    if(_model[Constants.FieldNames.DRAW_DOWN_OPTION] === Constants.DrawDownOption.CONSERVATIVE) {
        setAnswerToQuestionDrawDownPaymentsToDefault();
    }
}

function updateDrawDownTypeOptions() {
    _model.sameAsInvestAllocationOption = '';
    clearInvestmentOptionList(_model[Constants.FieldNames.INVESTMENT_OPTION_LIST]);
    _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] = '0.00';
    _.remove(_formError.errorList, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); });
}

function updateGrossAmount(value) {

    if (value) {
        value = Validators.currencyValidation(value.toString()) ? Formatter.currencyFormatWithoutRounding(value.toString()) : value;
        _model[Constants.FieldNames.PAYMENT_GROSS_AMOUNT] = value;
    }
}

function updateDeferPayments(value) {
    _model[Constants.FieldNames.DEFER_PAYMENTS_TEXT] = '';
    _model[Constants.FieldNames.DEFER_PAYMENTS_WITHIN_FYEAR_TEXT] = '';
    if (value === ApplicationConstants.YesNoValues.YES && _isMonthOtherThanJune && _model[Constants.FieldNames.PAYMENT_FREQUENCY] === Constants.PaymentFrequency.YEARLY && _model[Constants.FieldNames.YEARLY_FREQUENCY_DATE]) {
        _model[Constants.FieldNames.DEFER_PAYMENTS_WITHIN_FYEAR_TEXT] = _isWithinFinancialYear
            ? ContentService.getContent().pensionPayment.deferPaymentsWithinFYearTextReview
            : ContentService.getContent().pensionPayment.deferPaymentsNotWithinFYearTextReview;
    } else if (!_isWithinFinancialYear && value === ApplicationConstants.YesNoValues.NO && _model[Constants.FieldNames.PAYMENT_FREQUENCY] === Constants.PaymentFrequency.YEARLY && _model[Constants.FieldNames.YEARLY_FREQUENCY_DATE]) {
        _model[Constants.FieldNames.DEFER_PAYMENTS_TEXT] = ContentService.getContent().pensionPayment.deferPaymentsMessageTextReview;
    }
}

function updatePensionPaymentModel(fieldId, value, componentId) {

    _model[fieldId] = Mapper.toTrim(value);
    switch (fieldId) {
        case Constants.FieldNames.ACC_START_OPT:
            setAccStartOpt(value);
            break;
        case Constants.FieldNames.SELECTED_BANK_ACC:
            updateBankAccount(value);
            break;
        case Constants.FieldNames.PAYMENT_FREQUENCY:
            updatePaymentFrequency(fieldId, value);
            break;
        case Constants.FieldNames.YEARLY_FREQUENCY_DATE:
            updatePaymentFrequencyDate(value);
            break;
        case Constants.FieldNames.DEFER_PAYMENTS:
            updateDeferPayments(value);
            break;
        case Constants.FieldNames.PAYMENT_LEVEL_OPTIONS:
            updatePaymentLevelOptions(value);
            break;
        case Constants.FieldNames.ANNUAL_INCREASE_OPTIONS:
            updateAnnualIncreaseOptions(value);
            break;
        case Constants.FieldNames.ANNUAL_PERCENTAGE:
            updateAnnualPercentage(value);
            break;
        case Constants.FieldNames.PAYMENT_GROSS_AMOUNT:
            updateGrossAmount(value);
            break;
        case Constants.FieldNames.DRAW_DOWN_OPTION:
            updateDrawDownOption();
            showOrHideMessageForDrawDownOption();
            break;
        case Constants.FieldNames.DRAW_DOWN_TYPE_OPTION:
            updateDrawDownTypeOptions();
            break;
        default:
            break;
    }
    if (fieldId !== Constants.FieldNames.DRAW_DOWN_OPTION)
        validateFields(fieldId, value, componentId);
    showOrHideBankAccountMsg();
}

function getBankDetails(summary) {
    let dd = _.find(summary, { paymentMethod: FundingYourProductConstants.ContributionValues.DIRECT_D });
    if (dd && dd.accountNumber) {
        return dd;
    }
}

function setBankAccountsList(summary) {

    let data = getBankDetails(summary);

    if (data) {
        if (_model[Constants.FieldNames.NAME_ACC_HOLDER] === '' && _model[Constants.FieldNames.BRANCH_NUMBER] === '' && _model[Constants.FieldNames.ACC_NUMBER] === '' && (!_model[Constants.FieldNames.selectedBankAccount] || _model[Constants.FieldNames.selectedBankAccount] === Constants.nominateOtherAccount)) {
            if (_bankAccounts.length === 1) {
                _bankAccounts.unshift({
                    accountHolder: data.accountHolderName, branchNumber: data.branchNumber, accountNumber: data.accountNumber, value: data.accountNumber, id: data.accountNumber
                });
                updatePensionPayment({
                    accountHolderName: data.accountHolderName, branchNumber: data.branchNumber, accountNumber: data.accountNumber, selectedBankAccount: data.accountNumber
                });
            }
        }
    }

    showOrHideBankAccountMsg();
}

function clearSectionAndFormErrorList() {

    _formError = {
        errorList: []
    };
    _summaryError = [];
}

function validateForm() {

    if (_productId === ApplicationConstants.ProductIds.PENSION) {
        const copytext = ContentService.getContent().pensionPayment.pensionPaymentSectionSummary;
        const ids = Constants.pensPayment + '{x}';
        const data = _.cloneDeep(_model);
        const rules = getRules();

        clearSectionAndFormErrorList();

        if(_model.accountStartDateOption === Constants.AccountStart.ASAP) {
            delete data.accountStartDate;
            delete rules.accountStartDate;
        }

        if(_model.yearlyFrequencyDate === '' && _model.paymentFrequency !== Constants.PaymentFrequency.YEARLY) {
            delete data.yearlyFrequencyDate;
            delete rules.yearlyFrequencyDate;
        }

        _formError.errorList = Validators.validateAll(data, rules, _customAttributeName, ids);
        _summaryError = Validators.validateAll(data, rules, _customAttributeName, ids, copytext);

        if (!hasErrorDrawDownOption()) {
            validatePensionPaymentInvestmentOption(_model[Constants.FieldNames.DRAW_DOWN_TYPE_OPTION]);
        }
    }
}

function hasErrorDrawDownOption() {

    if (_model[Constants.FieldNames.DRAW_DOWN_OPTION] === Constants.DrawDownOption.CUSTOM && !_model[Constants.FieldNames.INVESTMENT_OPTION_LIST].length) {
        _formError.errorList.push({id: Constants.pensPayment + Constants.FieldNames.DRAW_DOWN_OPTION, message: ContentService.getContent().pensionPayment.drawDownOptionsText, type: 'error'});
        _summaryError.push({id: Constants.pensPayment + Constants.FieldNames.DRAW_DOWN_OPTION, message: ContentService.getContent().pensionPayment.investmentAllocationSummary, clickable: ContentService.getContent().pensionPayment.pensionPaymentSectionSummary});
        return true;
    }
    return false;
}

function filterPaymentLevelsOption(data) {

    if (!_isPreRetirement) {
        data = data.filter(function (element) {
            return element.showNotPreRetirement;
        });
    }
    return data;
}

function initStore() {
    _model = _.cloneDeep(Constants.PensionPaymentData);
    _frequencyDatesData = Helper.getDatesInAYear();
    _paymentLevelsOption = filterPaymentLevelsOption(Constants.PaymentLevelsData);
    _annualIncreaseOption = Constants.AnnualIncreaseData;
    _investmentOptionList = [];
    _showInvAllocationOptions = true;
    _showInSpecieOptions = false;
    _inSpecieOptions = [];
    _invAllocationOptions = [];
    _emptyOrder = [];

    clearSectionAndFormErrorList();
    _bankAccounts = [{
        accountHolder: 'Nominate another account',
        branchNumber: '',
        accountNumber: '',
        value: Constants.nominateOtherAccount,
        id: Constants.nominateOtherAccount
    }];
}

function setProductId(productId) {
    _productId = productId;
}

function setRetirementOption(value) {

    _isPreRetirement = value === PensionTypeConstants.PensionTypeSelectionNames.PRE_RETIREMENT;
    _paymentLevelsOption = filterPaymentLevelsOption(Constants.PaymentLevelsData);
    _model[Constants.FieldNames.PAYMENT_LEVEL_OPTIONS] = Constants.PaymentLevels.MINIMUM;
    _model[Constants.FieldNames.PAYMENT_GROSS_AMOUNT] = '';
    Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.PAYMENT_GROSS_AMOUNT);
}

function showOrHideMessageForDrawDownOption() {

    if (!hasErrorDrawDownOption()) {
        Validators.clearErrorListForField(_formError.errorList, Constants.pensPayment + Constants.FieldNames.DRAW_DOWN_OPTION);
        Validators.clearErrorListForField(_summaryError, Constants.pensPayment + Constants.FieldNames.DRAW_DOWN_OPTION);
    }
}

function setInvestmentOptionList(invAllocationOptions) {
    if (!_model) {
        initStore()
    }
    let invAllocationAddedOrRemoved = invAllocationOptions.length !== _invAllocationOptions.length;
    _invAllocationOptions = _.cloneDeep(invAllocationOptions);
    setOptions();

    if (invAllocationAddedOrRemoved) {
        clearInvestmentOptionList(_investmentOptionList);
        _model[Constants.FieldNames.INVESTMENT_OPTION_LIST] = _investmentOptionList;
        _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] = '0.00';

        _.remove(_formError.errorList, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix) || n.id === Constants.pensPayment + Constants.FieldNames.DRAW_DOWN_TYPE_OPTION; });
        if (_investmentOptionList.length === 0 && _model[Constants.FieldNames.DRAW_DOWN_OPTION] === Constants.DrawDownOption.CUSTOM) {
            _model[Constants.FieldNames.DRAW_DOWN_TYPE_OPTION] = '';
        }
    }
    showOrHideMessageForDrawDownOption();
}

function setInSpecieModel(data) {
    var newShowInSpecieOptions = data.inSpecieModel.setUp === ApplicationConstants.YesNoValues.YES;
    var newShowCustomOptions = data.inSpecieModel.setUp === ApplicationConstants.YesNoValues.NO ||
            (data.inSpecieModel.setUp === ApplicationConstants.YesNoValues.YES &&
            data.inSpecieModel.externalAccountQuestion !== ApplicationConstants.YesNoValues.YES);

    var isInSpecieSwitched = _showInSpecieOptions !== newShowInSpecieOptions || _showInvAllocationOptions !== newShowCustomOptions ;

    _showInvAllocationOptions = newShowCustomOptions;
    _showInSpecieOptions = newShowInSpecieOptions;

    var accounts = _.cloneDeep(data.inSpecieAccounts);
    var investmentOptions = _.flatten(_.map(accounts, 'investmentOptions'));
    _inSpecieOptions = _.reduce(investmentOptions, function (result, value) {
        if (_.isString(value)) {
            value = value.replace(/,/g, '');
        }
        if (!_.find(result, { id: value.id }) && parseFloat(value.balance) > 0) {
            result.push(value);
        }
        return result;
    }, []);
    setOptions();

    if (isInSpecieSwitched) {
        clearInvestmentOptionList(_investmentOptionList);
        _model[Constants.FieldNames.INVESTMENT_OPTION_LIST] = _investmentOptionList;
        _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] = '0.00';

        _.remove(_formError.errorList, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); });
        if (_investmentOptionList.length === 0 && _model[Constants.FieldNames.DRAW_DOWN_OPTION] === Constants.DrawDownOption.CUSTOM) {
            _model[Constants.FieldNames.DRAW_DOWN_TYPE_OPTION] = '';
        }
    }

    if (_showInSpecieOptions) {
        let indexOfManuallyInputtedAccount = _.findIndex(accounts,
            function(o) {
                return o.isExistingAccount === false;
            });

        _isCustomOrderDisabled = (indexOfManuallyInputtedAccount > -1);
        if (_isCustomOrderDisabled) {
            clearInvestmentOptionList(_investmentOptionList);
            _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] = '0.00';
            _model[Constants.FieldNames.DRAW_DOWN_OPTION] = Constants.DrawDownOption.CONSERVATIVE;
            _model[Constants.FieldNames.DRAW_DOWN_TYPE_OPTION] = '';
        }
    } else {
        _isCustomOrderDisabled = false
    }

    showOrHideMessageForDrawDownOption();

}

function setOptions() {
    if (_showInvAllocationOptions && _showInSpecieOptions){
        _investmentOptionList = _.cloneDeep(_invAllocationOptions);
        _.forEach(_inSpecieOptions,
            function (s) {
                if (!_.find(_investmentOptionList, { id: s.id })) {
                    _investmentOptionList.push(s);
                }
            });
    }else if (_showInvAllocationOptions){
        _investmentOptionList = _.cloneDeep(_invAllocationOptions);
    }else if (_showInSpecieOptions){
        _investmentOptionList = _.cloneDeep(_inSpecieOptions);
    }else{
        _investmentOptionList = []
    }
}

function clearInvestmentOptionList(investmentOptionList) {

    _.forEach(investmentOptionList, function(o) {
        o.isValid = true;
        o.weight = '';
        o.order = '';
    });

}

function setAnswerToQuestionDrawDownPaymentsToDefault() {

    _model[Constants.FieldNames.DRAW_DOWN_TYPE_OPTION] = '';
    _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] = '0.00';
    clearInvestmentOptionList(_model[Constants.FieldNames.INVESTMENT_OPTION_LIST]);
}

function updatePensionPaymentInvestmentOption(index, value) {

    let property = _model[Constants.FieldNames.DRAW_DOWN_TYPE_OPTION] === Constants.DrawDownTypeOption.ORDER? 'order' : 'weight';

    index = index.split('-');

    _model.investmentOptionList[index[1]][property] = Mapper.toTrim(value);

    validatePensionPaymentInvestmentOption(property, parseInt(index[1]));
}

function validatePensionPaymentInvestmentOption(property, index = -1) {

    let investmentOptionsFormError = [],
        investmentOptionsSummaryError = [];
    if (property) {
        _.remove(_formError.errorList, function (n) { return n.message !== ContentService.getContent().pensionPayment.investmentAllocationMustNumberAll });
        _.remove(_summaryError, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); });
        if (property === 'order') {
            validatePensionPaymentInvestmentOptionByOrder(index);
        } else {
            validatePensionPaymentInvestmentOptionByPercentage();
        }
        investmentOptionsFormError = _.filter(_formError.errorList, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); }),
        investmentOptionsSummaryError = _.filter(_summaryError, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); }),

        investmentOptionsFormError = _.sortBy(investmentOptionsFormError, 'index');
        investmentOptionsSummaryError = _.sortBy(investmentOptionsSummaryError, 'index');
        _.remove(_formError.errorList, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); });
        _.remove(_summaryError, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); });

        _formError.errorList = _(_formError.errorList).concat(investmentOptionsFormError).value();
        _summaryError = _(_summaryError).concat(investmentOptionsSummaryError).value();
    }
}

function validatePensionPaymentInvestmentOptionByOrder(index) {

    const count = _model[Constants.FieldNames.INVESTMENT_OPTION_LIST].length;
    const copyText = ContentService.getContent().pensionPayment;

    _.forEach(_model[Constants.FieldNames.INVESTMENT_OPTION_LIST], function(data, i) {
        if (data.order) {
            if (Validators.isNumeric(data.order) && data.order % 1 === 0) {
                if (data.order >= 1 && data.order <= count) {
                    let duplicates = [];

                    _.forEach(_model[Constants.FieldNames.INVESTMENT_OPTION_LIST], function(o, i) {
                        if (o.order === data.order) {
                            duplicates.push(i);
                        }
                    });

                    if (_.includes(duplicates, i) && i !== _.first(duplicates)) {
                        data.isValid = false;

                        if (_.findIndex(_formError.errorList, function(n){ return n.message === copyText.investmentAllocationInvalidCharacterAndNumber; }) < 0) {
                            _formError.errorList.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationInvalidCharacterAndNumber, type: 'error', index: i});
                            _summaryError.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationInvalidCharacterAndNumberSummary, clickable: copyText.pensionPaymentSectionSummary, index: i});
                        }
                    } else {
                        data.isValid = true;
                    }
                } else {
                    data.isValid = false;

                    if (_.findIndex(_formError.errorList, function(n){ return n.message === copyText.investmentAllocationInvalidCharacterAndNumber; }) < 0) {
                        _formError.errorList.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationInvalidCharacterAndNumber, type: 'error', index: i});
                        _summaryError.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationInvalidCharacterAndNumberSummary, clickable: copyText.pensionPaymentSectionSummary, index: i});
                    }
                }
            } else {
                data.isValid = false;

                if (_.findIndex(_formError.errorList, function(n){ return n.message === copyText.investmentAllocationInvalidCharacterAndNumber; }) < 0) {
                    _formError.errorList.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationInvalidCharacterAndNumber, type: 'error', index: i});
                    _summaryError.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationInvalidCharacterAndNumberSummary, clickable: copyText.pensionPaymentSectionSummary, index: i});
                }
            }
            _.remove(_emptyOrder, function (n) { return i === n && n === index; });
        } else {
            if (index !== -1 && index === i || index === -1) {
                data.isValid = false;

                if (!_.includes(_emptyOrder, index)) {
                    if (index < 0) {
                        _emptyOrder.push(i);
                    } else {
                        _emptyOrder.push(index);
                    }
                }
                if (_.findIndex(_formError.errorList, function(n){ return n.message === copyText.investmentAllocationMustNumberAll; }) < 0) {
                    _formError.errorList.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationMustNumberAll, type: 'error', index: i});
                    _summaryError.push({id: Constants.pensPaymentInvOptPrefix + i, message: copyText.investmentAllocationMustNumberAllSummary, clickable: copyText.pensionPaymentSectionSummary, index: i});
                }
            }
        }
    });
    if (_emptyOrder.length) {
        let error = _.find(_formError.errorList, function(n) { return n.message === ContentService.getContent().pensionPayment.investmentAllocationMustNumberAll; });
        if (error) {
            error.index = _emptyOrder[0];
        }
    }
    _.remove(_formError.errorList, function (n) {
        return n.message === ContentService.getContent().pensionPayment.investmentAllocationMustNumberAll && !_emptyOrder.length;
    });
}

function validatePensionPaymentInvestmentOptionByPercentage() {

    let total = _.sum(_model.investmentOptionList, function (n) { return n.weight; }),
        sumInvestmentAllocationWeight = _.round(total, 2);

    _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] = Formatter.currencyFormatWithoutRounding(sumInvestmentAllocationWeight);
    validateTotalAllocationPercentage();
}

function validateTotalAllocationPercentage() {

    let sumOfTotalPercentage = _.round(_model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE], 2),
        errorExistsInSection = _.filter(_formError.errorList, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); }),
        errorExistsInForm = _.filter(_summaryError, function (n) { return _.startsWith(n.id, Constants.pensPaymentInvOptPrefix); });

    if(!errorExistsInSection.length && !errorExistsInForm.length) {
        if (sumOfTotalPercentage > 0 && sumOfTotalPercentage < 100 || sumOfTotalPercentage > 100 || sumOfTotalPercentage <= 0 || !Validators.isNumeric(sumOfTotalPercentage)) {
            _formError.errorList.push({id: Constants.pensPaymentInvOptPrefix + 0, message: ContentService.getContent().pensionPayment.investmentAllocationMustTotal100, type: 'error', index: _model[Constants.FieldNames.INVESTMENT_OPTION_LIST].length});
            _summaryError.push({id: Constants.pensPaymentInvOptPrefix + 0, message: ContentService.getContent().pensionPayment.investmentAllocationMustTotal100Summary, clickable: ContentService.getContent().pensionPayment.pensionPaymentSectionSummary, index: _model[Constants.FieldNames.INVESTMENT_OPTION_LIST].length});
            _.forEach(_model[Constants.FieldNames.INVESTMENT_OPTION_LIST], function(o){ o.isValid = false; });
        } else if (sumOfTotalPercentage === 100) {
            _.forEach(_model[Constants.FieldNames.INVESTMENT_OPTION_LIST], function(o){ o.isValid = true; });
        }
    }
}

function retrievePensionPayment(applicationData) {
    initStore();
    const originationSource = _.get(applicationData, 'originationSource', ApplicationConstants.OriginationSources.FIRSTNET);
    if (originationSource === ApplicationConstants.OriginationSources.IRESS) {
        const invAllocations = _.get(applicationData, 'form.investmentAllocation.investmentOptions', []);
        setInvestmentOptionList(invAllocations);
    } else {
        const form = _.get(applicationData, 'form.pensionPayment');
        if (form) {
            _model = _.cloneDeep(form);

            if (!_model[Constants.FieldNames.ACC_START_DATE]) {
                _model[Constants.FieldNames.ACC_START_DATE] = '';
            }
            if (!_model[Constants.FieldNames.INVESTMENT_OPTION_LIST]) {
                _model[Constants.FieldNames.INVESTMENT_OPTION_LIST] = [];
            } else {
                _.forEach(_model[Constants.FieldNames.INVESTMENT_OPTION_LIST], function(o) {
                    if (_model[Constants.FieldNames.DRAW_DOWN_TYPE_OPTION] === Constants.DrawDownTypeOption.PERCENTAGE) {
                        if (o.weight % 1 === 0) {
                            o.weight = o.weight.toFixed(2).toString();
                        }
                    }
                    o.isValid = true;
                });
            }
            if (_model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] && _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] % 1 === 0) {
                _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE] = _model[Constants.FieldNames.TOTAL_ALLOCATION_PERCENTAGE].toFixed(2).toString();
            }
            if (_model[Constants.FieldNames.PAYMENT_GROSS_AMOUNT] && _model[Constants.FieldNames.PAYMENT_GROSS_AMOUNT] % 1 === 0) {
                _model[Constants.FieldNames.PAYMENT_GROSS_AMOUNT] = _model[Constants.FieldNames.PAYMENT_GROSS_AMOUNT].toFixed(2).toString();
            }
            if (_model[Constants.FieldNames.SELECTED_BANK_ACC]) {
                _bankAccounts.unshift({
                    accountHolder: _model[Constants.FieldNames.NAME_ACC_HOLDER], branchNumber: _model[Constants.FieldNames.BRANCH_NUMBER], accountNumber: _model[Constants.FieldNames.ACC_NUMBER], value: _model[Constants.FieldNames.ACC_NUMBER], id: _model[Constants.FieldNames.ACC_NUMBER]
                });
            }
            if (_model[Constants.FieldNames.DEFER_PAYMENTS] && _model[Constants.FieldNames.PAYMENT_FREQUENCY] === Constants.PaymentFrequency.YEARLY && _model[Constants.FieldNames.YEARLY_FREQUENCY_DATE]) {
                updatePaymentFrequencyDate(_model[Constants.FieldNames.YEARLY_FREQUENCY_DATE]);
            }
        }
    }
}

function setPensionAllocationSameAsInvestmentAllocation(option) {
    if (option === ApplicationConstants.YesNoValues.YES && !_showInSpecieOptions) {
        _.forEach(_model.investmentOptionList, function(o) {
            var origAllocation = _.find(_invAllocationOptions, { id: o.id });
            if (origAllocation) {
                o.weight = origAllocation.weight;
            }
        });

        validatePensionPaymentInvestmentOptionByPercentage();
    } else {
        _.forEach(_model.investmentOptionList, function (o) {
            o.weight = '';
        });
        _model.totalAllocationPercentage = '0.00';
    }  
    _model.sameAsInvestAllocationOption = option;
}

class PensionPaymentStoreClass extends EventEmitter {

    constructor() {
        super();
    }

    emitChange() {
        return setTimeout(()=>this.emit(CHANGE_EVENT), 0);
    }

    addChangeListener(callback) {
        this.on(CHANGE_EVENT, callback);
    }

    removeChangeListener(callback) {
        this.removeListener(CHANGE_EVENT, callback);
    }

    getPensionPaymentModel() {
        return _.cloneDeep(_model);
    }

    getBankAccountsList() {
        return _bankAccounts;
    }

    getFormError() {
        return _formError;
    }

    getBankAccountDetailsMsg() {
        return _showBankAccountDetailsMsg;
    }

    getfrequencyDatesData() {
        return _frequencyDatesData;
    }

    getIsPreRetirement() {
        return _isPreRetirement;
    }

    getPaymentLevelsOption() {
        return _paymentLevelsOption;
    }

    getAnnualIncreaseOption() {
        return _annualIncreaseOption;
    }

    pensionPaymentErrorMessages() {
        return _summaryError;
    }

    getIsCustomOrderDisabled() {
        return _isCustomOrderDisabled;
    }

    getIsInspecieYes() {
        return _showInSpecieOptions;
    }
}

const PensionPaymentStore = new PensionPaymentStoreClass();

PensionPaymentStore.dispatchToken = AppDispatcher.register((action) => {

    switch (action.actionType) {
        case ActionTypes.UPDATE_PENSION_PAYMENT_MODEL:
            updatePensionPaymentModel(action.fieldId, action.value, action.componentId);
            break;
        case ActionTypes.UPDATE_PENSION_PAYMENT_INVESTMENT_OPTION:
            updatePensionPaymentInvestmentOption(action.index, action.value);
            break;
        case ApplicationConstants.ActionTypes.IS_FUNDING_SUMMARY:
            setBankAccountsList(action.summary);
            break;
        case ApplicationConstants.ActionTypes.IS_PRE_RETIREMENT:
            setRetirementOption(action.value);
            break;
        case ApplicationConstants.ActionTypes.SEND_INVESTMENT_OPTIONS:
            setInvestmentOptionList(action.investmentOptionList);
            break;
        case ApplicationConstants.ActionTypes.SEND_INSPECIE_DATA:
            setInSpecieModel(action.data);
            break;
        case ApplicationConstants.ActionTypes.VALIDATE_APPLICATION:
            validateForm();
            break;
        case ApplicationConstants.ActionTypes.NEW_APPLICATION_INITIALISED:
            initStore();
            setProductId(action.productId);
            break;
        case ApplicationConstants.ActionTypes.APPLICATION_RETRIEVE_SUCCESS:
            setProductId(action.applicationData.productId);
            retrievePensionPayment(action.applicationData);
            break;
        case ActionTypes.SET_PENSION_ALLOCATION_SAME_AS_INV_ALLOCATION:
            setPensionAllocationSameAsInvestmentAllocation(action.option);
            break;
        default:
            return true;
    }

    PensionPaymentStore.emitChange();
    return true;
});

export default PensionPaymentStore;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./stores/PensionPaymentStore.js